/* eslint-disable no-nested-ternary */
/**
 * KPI 事件设置弹窗：交互与舆情 MetricsChart/EventSetting 对齐；
 * 未确认时默认全选；已确认时从 bubbleEventValue 恢复勾选态。
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox, Icon, Button } from 'tea-component';
import { getLocale } from 'umi';
import { getEventName } from '@op/pages/Overview/KeyMetrics/utils';
import { arrayToSimpleObj, sortTimeListFunc } from '@op/utils';
import CardCommon from '@op/components/CardCommon';
import { getRealXAxisValue } from '@op/utils/chart';
import { formatTimeToShow } from '@op/utils/time';
import { useSyncState } from '@op/hooks/common';
import { useUpdateEffect } from 'ahooks';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import '@op/pages/Overview/KeyMetrics/MentionsSentiment/components/EventSetting/index.scss';
import {
  buildEventSettingSelectValueFromBubbleEvent,
  getOnlyOneKey,
} from './kpiEventUiUtils';
import type {
  IEventMetricTrendEventSettingModalProps,
  IEventSettingModalStrings,
} from './types';

export type { IEventSettingConfirmPayload } from './types';

const languageMap: Record<'en-US' | 'zh-CN', IEventSettingModalStrings> = {
  'en-US': {
    caption: 'Events Setting',
    tips: 'When too many events are selected, some may not be fully displayed on the chart due to space constraints.',
    showEventAxis: 'Show event axis',
    showEventBubbles: 'Show event bubbles',
    showExperimentEventDetailFlat:
      'Show [实验] event details',
    emptyNoEvents: 'There are no events scheduled for this period.',
    setDisplayEvents: 'Set display events',
    clearAll: 'Clear All',
    selectAll: 'Select All',
    event: 'Event',
    confirm: 'Confirm',
    cancel: 'Cancel',
  },
  'zh-CN': {
    caption: '事件设置',
    tips: '选择的事件过多时，受空间限制，图表上可能无法完整展示所有事件。',
    showEventAxis: '显示事件轴',
    showEventBubbles: '显示事件气泡',
    showExperimentEventDetailFlat:
      '显示[实验]事件详情',
    emptyNoEvents: '该时段内没有已安排的事件。',
    setDisplayEvents: '设置展示事件',
    clearAll: '全部清除',
    selectAll: '全选',
    event: '事件',
    confirm: '确定',
    cancel: '取消',
  },
};

function getEventSettingModalText(locale: string): IEventSettingModalStrings {
  return locale === 'zh-CN' ? languageMap['zh-CN'] : languageMap['en-US'];
}

const EventMetricTrendEventSettingModal: React.FC<
IEventMetricTrendEventSettingModalProps
> = ({
  visible,
  close,
  confirm,
  data,
  timeFormat,
  showEventAxisValue,
  showEventBubblesValue,
  showExperimentEventDetailFlatValue,
  dateType,
  xAxisData,
  requestLoading = false,
  requestError = false,
  hideAxisBubbleToggles = false,
  bubbleEventValue,
  bubbleSelectionCommitted = false,
  showExperimentEventDetailFlatType = "实验"
}) => {
  const lang = getLocale();
  const text = useMemo(() => getEventSettingModalText(lang), [lang]);

  const {
    state: selectValue,
    setState: setSelectValue,
    syncStates: syncSelectValue,
    revertToPrevious: revertSelectValue,
    initStates: initSelectValue,
  } = useSyncState<Record<string, string[]>>({});

  const isHaveExperiment = useMemo(() => {
    return data?.[showExperimentEventDetailFlatType]?.length > 0;
  }, [data, showExperimentEventDetailFlatType]);

  const {
    state: showEventAxis,
    setState: setShowEventAxis,
    syncStates: syncShowEventAxis,
    revertToPrevious: revertShowEventAxis,
    initStates: initShowEventAxis,
  } = useSyncState<boolean>(showEventAxisValue);

  const {
    state: showEventBubbles,
    setState: setShowEventBubbles,
    syncStates: syncShowEventBubbles,
    revertToPrevious: revertShowEventBubbles,
    initStates: initShowEventBubbles,
  } = useSyncState<boolean>(showEventBubblesValue);

  const {
    state: showExperimentEventDetailFlat,
    setState: setShowExperimentEventDetailFlat,
    syncStates: syncShowExperimentEventDetailFlat,
    revertToPrevious: revertShowExperimentEventDetailFlat,
    initStates: initShowExperimentEventDetailFlat,
  } = useSyncState<boolean>(showExperimentEventDetailFlatValue);

  useUpdateEffect(() => {
    initShowEventAxis(showEventAxisValue);
  }, [showEventAxisValue]);

  useUpdateEffect(() => {
    initShowEventBubbles(showEventBubblesValue);
  }, [showEventBubblesValue]);

  useUpdateEffect(() => {
    initShowExperimentEventDetailFlat(showExperimentEventDetailFlatValue);
  }, [showExperimentEventDetailFlatValue]);

  const closeWindow = (): void => {
    close();
    revertSelectValue();
    revertShowEventBubbles();
    revertShowEventAxis();
    revertShowExperimentEventDetailFlat();
  };

  const syncAllStates = (): void => {
    syncSelectValue();
    syncShowEventAxis();
    syncShowEventBubbles();
    syncShowExperimentEventDetailFlat();
  };

  const changeSelectValue = (
    value: Record<string, string[]> | string[],
    key?: string,
  ): void => {
    if (key) {
      setSelectValue({ ...selectValue, [key]: value as string[] });
    } else {
      setSelectValue(value as Record<string, string[]>);
    }
  };

  const [levelOneActive, setLevelOneActive] = useState('');
  const [levelOneOptions, setLevelOneOptions] = useState<
  Array<{
    name: string;
    name_cn: string;
    value: string;
    children: Array<string>;
  }>
  >([]);

  const getCallbackEventList = (
    sel: Record<string, string[]>,
  ): Record<string, unknown[]> => {
    const selectedList = Object.values(sel).flat();
    const flatData = Object.values(data || {}).flat() as Record<
    string,
    unknown
    >[];
    const list = flatData.filter((item) =>
      selectedList.includes(getOnlyOneKey(item)),
    );

    list.sort(sortTimeListFunc('start_time', 'desc'));
    const result: Record<string, unknown[]> = {};

    list.forEach((item: Record<string, unknown>) => {
      const time = getRealXAxisValue({
        xAxis: String(item.start_time ?? ''),
        dateType,
        format: timeFormat,
        xAxisData,
      });
      if (!result[time]) {
        result[time] = [];
      }
      result[time].push(item);
    });

    return result;
  };

  /** 与 data 对象引用解耦：滚动等导致父组件重渲染时 data 常为新引用，若依赖 [data] 会反复误重置 */
  const dataResetKey = JSON.stringify(
    Object.keys(data || {})
      .sort()
      .map((k) =>
        (data?.[k] ?? []).map((row: Record<string, unknown>) =>
          getOnlyOneKey(row),
        ),
      ),
  );

  /** 已确认筛选指纹：data 结构变化时仍从 bubbleEvent 恢复勾选态 */
  const bubbleEventSelectKey = useMemo(() => {
    if (!bubbleSelectionCommitted) {
      return '';
    }
    return JSON.stringify(
      Object.keys(bubbleEventValue ?? {})
        .sort()
        .map(k =>
          (bubbleEventValue?.[k] ?? []).map((row: Record<string, unknown>) =>
            getOnlyOneKey(row),
          ),
        ),
    );
  }, [bubbleEventValue, bubbleSelectionCommitted]);

  const buildInitialSelectValue = (): Record<string, string[]> =>
    buildEventSettingSelectValueFromBubbleEvent(
      data,
      bubbleEventValue,
      bubbleSelectionCommitted,
    );

  useEffect(() => {
    const selected = buildInitialSelectValue();
    const opts = Object.keys(data || {}).map((key) => {
      const list = (data[key] || []) as Record<string, unknown>[];
      const item = list[0] || {};
      const children = list.map((row) => getOnlyOneKey(row));

      return {
        name: key,
        name_cn: String(
          (item as { event_type_cn?: string; }).event_type_cn || key,
        ),
        value: key,
        children,
      };
    });

    initSelectValue(selected);
    setLevelOneActive(opts?.[0]?.value || '');
    setLevelOneOptions(opts);
    // useSyncState 的 initSelectValue 不能进依赖；用 dataResetKey / bubbleEventSelectKey 代替 [data]
  }, [dataResetKey, bubbleEventSelectKey]);

  /** 每次打开弹窗时从已生效的 bubbleEvent 同步勾选 UI */
  useUpdateEffect(() => {
    if (!visible) {
      return;
    }
    initSelectValue(buildInitialSelectValue());
  }, [visible]);

  const { levelOneSelect, levelOneIndeterminate, levelOneIsClear } =
    useMemo(() => {
      const result: {
        levelOneSelect: Record<string, boolean>;
        levelOneIndeterminate: Record<string, boolean>;
        levelOneIsClear: boolean;
      } = {
        levelOneSelect: {},
        levelOneIndeterminate: {},
        levelOneIsClear: false,
      };

      for (const key in data) {
        const element = (data[key] || []) as unknown[];
        const selected = selectValue[key] || [];

        if (element.length === selected.length) {
          result.levelOneSelect[key] = true;
          result.levelOneIsClear = true;
        } else if (selected.length > 0) {
          result.levelOneIndeterminate[key] = true;
          result.levelOneIsClear = true;
        } else {
          result.levelOneSelect[key] = false;
        }
      }

      return result;
    }, [selectValue, data]);

  const handleLevelOneToggleClearBtn = (): void => {
    if (levelOneIsClear) {
      const cleared: Record<string, string[]> = {};
      Object.keys(selectValue).forEach((key) => {
        cleared[key] = [];
      });
      changeSelectValue(cleared);
      return;
    }

    const item = arrayToSimpleObj(levelOneOptions, 'children', 'value');
    changeSelectValue(item);
  };

  const levelTwoOptions = useMemo(() => {
    const list = (
      (data?.[levelOneActive] || []) as Record<string, unknown>[]
    ).map((row) => ({
      ...row,
      value: getOnlyOneKey(row),
    }));
    list.sort(sortTimeListFunc('start_time', 'desc'));
    return list;
  }, [levelOneActive, data]);

  const { levelTwoSelect, levelTwoIsClear } = useMemo(() => {
    const value = selectValue[levelOneActive] || [];
    return {
      levelTwoSelect: value,
      levelTwoIsClear: value.length > 0,
    };
  }, [selectValue, levelOneActive]);

  const handleLevelTwoToggleClearBtn = (): void => {
    if (levelTwoIsClear) {
      changeSelectValue([], levelOneActive);
      return;
    }

    const item = levelOneOptions.find((o) => o.value === levelOneActive);
    changeSelectValue(item?.children || [], levelOneActive);
  };

  return (
    <Modal
      className="dbo-modal-event-setting"
      visible={visible}
      caption={text.caption}
      onClose={() => closeWindow()}
    >
      <Modal.Body>
        {requestError ? (
          <div className="dbo-event-setting dbo-event-setting--status">
            <Abnormal isLoading={false} isError style={{ minHeight: 320 }} />
          </div>
        ) : requestLoading ? (
          <div className="dbo-event-setting dbo-event-setting--status">
            <Abnormal isLoading style={{ minHeight: 320 }} />
          </div>
        ) : (
          <div className="dbo-event-setting dbc-region-select-container">
            <div className="dbo-event-setting-hd">
              <div className="dbo-event-setting-tips">{text.tips}</div>
              {/* {!hideAxisBubbleToggles && (
                <>
                  <Checkbox
                    value={showEventBubbles}
                    onChange={(value: boolean) => setShowEventBubbles(value)}
                  >
                    {text.showEventBubbles}
                  </Checkbox>
                  <Checkbox
                    value={showEventAxis}
                    onChange={(value: boolean) => setShowEventAxis(value)}
                  >
                    {text.showEventAxis}
                  </Checkbox>
                </>
              )} */}

              {
                isHaveExperiment &&   <Checkbox
                  value={showExperimentEventDetailFlat}
                  onChange={(value: boolean) =>
                    setShowExperimentEventDetailFlat(value)
                  }
                >
                  {text.showExperimentEventDetailFlat}
                </Checkbox>
              }
            
            </div>
            <div className="dbo-event-setting-bd">
              <CardCommon
                contentCard
                empty={levelOneOptions.length === 0}
                emptyHeight={280}
                hasEmptyTitle={false}
                emptyDescText={text.emptyNoEvents}
              >
                <div className="dbc-region-select-content-container">
                  <div className="dbc-region-select-content-wrap">
                    <div className="dbc-region-select-content-title-wrap">
                      <span>{text.setDisplayEvents}</span>
                      <span
                        className="dbc-region-select-button"
                        onClick={handleLevelOneToggleClearBtn}
                        role="presentation"
                      >
                        {levelOneIsClear ? text.clearAll : text.selectAll}
                      </span>
                    </div>
                    <div className="dbc-region-select-item-wrap">
                      {levelOneOptions.map((item) => (
                        <div
                          key={item.value}
                          onClick={() => setLevelOneActive(item.value)}
                          className={`dbc-region-select-checkbox ${
                            levelOneActive === item.value ? 'active' : ''
                          }`}
                          role="presentation"
                        >
                          <Checkbox
                            value={levelOneSelect[item.value]}
                            indeterminate={levelOneIndeterminate[item.value]}
                            onChange={(value: boolean) => {
                              setLevelOneActive(item.value);
                              const selected = value ? item.children : [];
                              changeSelectValue(selected, item.value);
                            }}
                          />
                          <div className="dbc-region-select-checkbox-name-wrap">
                            <span className="dbc-region-select-checkbox-name">
                              {getEventName(item, lang)} ({item.children.length}
                              )
                            </span>
                            <Icon type="arrowright" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="dbc-region-select-content-wrap dbc-region-select-content-wrap--right">
                    <div className="dbc-region-select-content-title-wrap">
                      <span className="dbc-region-select-content-country-title">
                        {text.event}
                      </span>
                      <span
                        className="dbc-region-select-button"
                        onClick={handleLevelTwoToggleClearBtn}
                        role="presentation"
                      >
                        {levelTwoIsClear ? text.clearAll : text.selectAll}
                      </span>
                    </div>
                    <div className="dbc-region-select-item-wrap">
                      <Checkbox.Group
                        value={levelTwoSelect}
                        onChange={(value: string[]) => {
                          changeSelectValue(value, levelOneActive);
                        }}
                      >
                        {levelTwoOptions.map(
                          (
                            item: Record<string, unknown> & { value?: string; },
                          ) => (
                            <Checkbox
                              name={String(item.value)}
                              key={String(item.value)}
                              className="dbc-region-select-checkbox"
                            >
                              <div className="dbc-region-select-checkbox-name-wrap">
                                <span className="dbc-region-select-checkbox-name">
                                  {getEventName(item, lang)}
                                </span>
                                <span className="dbc-region-select-checkbox-date">
                                  {formatTimeToShow({
                                    ...item,
                                    date_type: 'hourly',
                                    hourlyIsSame: true,
                                  })}
                                </span>
                              </div>
                            </Checkbox>
                          ),
                        )}
                      </Checkbox.Group>
                    </div>
                  </div>
                </div>
              </CardCommon>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          disabled={requestLoading || requestError}
          onClick={() => {
            if (requestLoading || requestError) {
              return;
            }
            syncAllStates();
            confirm({
              bubbleEvent: getCallbackEventList(selectValue),
              showEventAxis: hideAxisBubbleToggles
                ? showEventAxisValue
                : showEventAxis,
              showEventBubbles: hideAxisBubbleToggles
                ? showEventBubblesValue
                : showEventBubbles,
              showExperimentEventDetailFlat,
            });
          }}
        >
          {text.confirm}
        </Button>
        <Button type="weak" onClick={() => closeWindow()}>
          {text.cancel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EventMetricTrendEventSettingModal;
